import deepmerge from "deepmerge";
import { getIcons } from "./icons";
import { getGdDisplayIndex, objectFlatMap, reverseBoolean, getPlacementSettings, getRelativeSettings, traverse, getCascade, deleteNullish, toGoLang, updateNativeCmdArgs } from "./windowFunctions";
import { newAppDEntry } from "./newApp2Entry";
import { convertFinsembleKeyArrToGlueKeyString } from "../hotkey";
import { consoleLogger } from "./Logger";
const defaultUIComponent = {
    window: {
        addToWorkspace: false,
        affinity: "systemComponents",
        options: {
            autoShow: false,
            resizable: false,
            showTaskbarIcon: false,
            alwaysOnTop: true,
        },
    },
    component: {
        category: "system",
    },
    foreign: {
        services: {
            windowService: {
                shouldRegister: false,
            },
        },
        components: {
            "App Launcher": {
                launchableByUser: false,
            },
            "Window Manager": {
                FSBLHeader: false,
                persistWindowState: false,
            },
        },
    },
};
const defaultAppService = {
    hostManifests: {
        Finsemble: {
            bootParams: {
                autoStart: true,
                stage: "preuser"
            },
            window: {
                options: {
                    autoShow: false
                },
                frame: true
            },
            component: {
                singleton: true,
                spawnOnStartup: true
            },
            foreign: {
                components: {
                    "Window Manager": {
                        FSBLHeader: false
                    },
                    "WindowService": {
                        manageWindowMovement: false
                    },
                    "App Launcher": {
                        launchableByUser: false
                    }
                }
            }
        }
    }
};
const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
export const finsembleWhitelistToRegex = (finsemble) => {
    const entries = typeof finsemble === "string" ? [finsemble] : finsemble;
    const regExeHosts = entries.map((host) => `((http|https)\\:\\/\\/([a-zA-Z0-9_-]*\\.)*${escapeRegExp(host)}(\\/)+.*)`);
    const regExeOrigins = entries.map((origin) => `(${escapeRegExp(origin)}(\\/)+.*)`);
    const allowed = regExeHosts.concat(regExeOrigins).join("|");
    return entries.length ? { allowed: `^${allowed}$` } : {};
};
const isProtocolHandler = (str) => {
    const pattern = new RegExp("^(.+):\/\/(.*)$", "i");
    return !!pattern.test(str);
};
const splitCommandPath = (aliasDetails, fdc3Path) => {
    if (aliasDetails) {
        return aliasDetails;
    }
    if (!fdc3Path) {
        return {};
    }
    const splits = fdc3Path.split(/[\\/]/);
    const command = splits.pop();
    const path = splits.join("/");
    return { command, path };
};
export const stagePriorityMap = {
    "microkernel": ["core", 2000],
    "kernel": ["core", 1000],
    "pre-authentication": ["pre-sso", 1000],
    "authentication": ["sso", 1000],
    "appd-retrieval": ["post-sso", 7000],
    "system-preuser": ["post-sso", 6000],
    "preuser": ["post-sso", 5000],
    "preuser2": ["post-sso", 4000],
    "preuser3": ["post-sso", 3000],
    "preuser4": ["post-sso", 2000],
    "system-earlyuser": ["post-sso", 1500],
    "earlyuser": ["post-sso", 1000],
    "user": ["user", 1000]
};
const maybeSetRunPriority = (definition, hostManifest) => {
    if (definition.runPriority) {
        return definition;
    }
    if (!hostManifest) {
        return definition;
    }
    if (hostManifest.bootParams?.stage) {
        const finsembleStage = hostManifest.bootParams.stage ?? "user";
        const stage = stagePriorityMap[finsembleStage] ?? ["post-sso", 9000];
        definition.runPriority = {
            stage: stage[0],
            priority: stage[1],
            waitForInitialization: hostManifest.waitForInitialization,
            timeout: hostManifest.bootParams.timeout ?? 120000
        };
    }
    else if (definition.autoStart) {
        definition.runPriority = {
            stage: "post-sso",
            priority: 2
        };
    }
    return definition;
};
export const hostManifestToDefinition = (glue, hostManifest) => {
    const definition = {
        autoStart: hostManifest?.component?.spawnOnStartup ?? hostManifest?.bootParams?.autoStart ?? false
    };
    return maybeSetRunPriority(definition, hostManifest);
};
const maybeConvertToSSOApplication = (manifest, definition) => {
    const authenticationComponentName = manifest?.finsemble?.authentication?.startup?.component;
    if (authenticationComponentName === definition.name) {
        definition.name = "sso-application";
        definition.details.mode = "html";
        definition.details.allowClose = true;
    }
};
export const convertComponentToGlueDefinition = (glue, logger, name, manifest, hostManifest) => {
    logger.info(`config - Converting component config ${name} to iocd`, hostManifest);
    const { err, data } = hostManifestToGlueDetailsAndAsset(glue, logger, manifest, hostManifest);
    if (err || !data) {
        return { err };
    }
    const { details } = data;
    details.mode = (hostManifest.foreign?.components?.["Window Manager"]?.FSBLHeader ?? false) ? details.mode : "frameless";
    details.allowEnvVars = true;
    details.allowClearingCache = true;
    details.disableTransitions = true;
    details.transparent = false;
    details.nativeWindowOpenOptions = {
        allowChannels: false,
        allowFeedback: false,
        allowClose: false,
        showInTaskbar: false,
        allowMinimize: false,
        allowMaximize: false,
        allowClearingCache: true,
        allowEnvVariables: true,
        minWidth: 0,
        maxWidth: 1000000,
        minHeight: 0,
        maxHeight: 1000000,
        disableTransitions: true,
        hasSizeAreas: false,
        hidden: true,
        ignoreFromLayouts: true
    };
    const definition = {
        ...hostManifestToDefinition(glue, hostManifest),
        type: "window",
        details,
        ignoreSavedLayout: name !== "Toolbar",
        ignoreFromLayouts: true,
        allowAutoArrange: false,
        allowLogging: true,
        allowCapture: true,
        allowCLAAccess: true,
        name,
        hidden: reverseBoolean(hostManifest?.foreign?.components?.["App Launcher"]?.launchableByUser) ?? false,
        customProperties: {
            component: hostManifest
        }
    };
    if (name === "Toolbar") {
        definition.shell = true;
        definition.runPriority = {
            stage: "post-sso",
            priority: 1501,
            waitForInitialization: true,
            timeout: 120000
        };
    }
    if (name === "SystemTrayComponent") {
        definition.name = "custom-tray";
        definition.details.showInTaskbar = false;
        definition.details.width = definition.details.placement.width;
        definition.details.height = definition.details.placement.height;
        delete definition.details.placement;
        definition.runPriority = {
            stage: "post-sso",
            priority: 1000
        };
    }
    maybeConvertToSSOApplication(manifest, definition);
    const ioConnectManifest = hostManifest["io.Connect"] ?? hostManifest["IO.Connect"] ?? hostManifest["iocd"];
    return { data: deepmerge(definition, ioConnectManifest ?? {}) };
};
export const convertAppDToGlueDefinition = (glue, logger, appd, manifest) => {
    const ioConnectManifest = appd.hostManifests?.["Glue 42"] ?? appd.hostManifests?.["io.Connect"] ?? appd.hostManifests?.["IO.Connect"] ?? appd.hostManifests?.["iocd"];
    if (ioConnectManifest && !appd.hostManifests?.Finsemble) {
        return { data: ioConnectManifest };
    }
    const hostManifest = appd.hostManifests?.Finsemble;
    const features = getFinsembleWindowFeatures(manifest?.finsemble, hostManifest);
    const icons = getIcons(appd);
    const { err, data } = fdc3ToGlueDetailsAndAsset(glue, logger, manifest, appd);
    if (err || !data) {
        return { err };
    }
    const { details, asset, type } = data;
    const definition = deleteNullish({
        ...hostManifestToDefinition(glue, hostManifest),
        type,
        details,
        allowLogging: true,
        name: appd.appId,
        version: appd.version,
        allowCapture: true,
        allowCLAAccess: true,
        title: appd.title ?? hostManifest?.component?.displayName ?? appd.name ?? appd.appId,
        caption: appd.description,
        tooltip: appd.tooltip,
        ignoreSavedLayout: true,
        hidden: reverseBoolean(hostManifest?.foreign?.components?.["App Launcher"]?.launchableByUser) ?? false,
        ignoreFromLayouts: reverseBoolean(hostManifest?.foreign?.services?.windowService?.addToWorkspace) ?? false,
        allowAutoArrange: features.allowAutoArrange,
        service: reverseBoolean(hostManifest?.foreign?.services?.windowService?.addToWorkspace) ?? false,
        intents: convertFDC3IntentsToGlueIntents(appd.interop?.intents),
        reuseInLayouts: hostManifest?.window?.options?.isEvergreen ?? false,
        supportEmails: appd.supportEmail ? [appd.supportEmail] : undefined,
        keywords: appd.categories ?? appd.tags,
        allowMultiple: reverseBoolean(appd.hostManifests?.Finsemble?.component?.singleton),
        icon: icons.taskbarIcon?.path ?? icons.toolbarIcon?.path,
        customProperties: {
            appd
        },
    });
    if (asset) {
        definition.asset = asset;
    }
    maybeConvertToSSOApplication(manifest, definition);
    return { data: deepmerge(definition, ioConnectManifest ?? {}) };
};
const convertComponentsToGlueDefinition = (glue, logger, manifest, components) => {
    if (!components) {
        return [];
    }
    const definitions = [];
    for (const [key, value] of Object.entries(components)) {
        const { data: definition } = convertComponentToGlueDefinition(glue, logger, key, manifest, value);
        if (definition) {
            definitions.push(definition);
        }
    }
    return definitions;
};
const maybeAdjustRunPriorities = (definitions) => {
    definitions.forEach((definition) => {
        const { name } = definition;
        if (definitions.some((d) => (d.customProperties?.appd?.hostManifests?.Finsemble?.bootParams?.dependencies ?? []).includes(name))) {
            if (typeof (definition.runPriority) === "object" && definition.runPriority.priority) {
                definition.runPriority.priority++;
            }
        }
        ;
    });
};
export const convertAppsToGlueDefinition = (glue, logger, manifest, apps) => {
    if (!apps) {
        return [];
    }
    const definitions = [];
    for (const app of apps) {
        const { data: definition } = convertAppDToGlueDefinition(glue, logger, app, manifest);
        if (definition) {
            definitions.push(definition);
        }
    }
    maybeAdjustRunPriorities(definitions);
    return definitions;
};
const anyWindowManagementAllowed = (foreignWindowServiceConfig) => foreignWindowServiceConfig?.allowSnapping ||
    foreignWindowServiceConfig?.allowGrouping ||
    foreignWindowServiceConfig?.allowAutoArrange ||
    foreignWindowServiceConfig?.allowTabbing ||
    foreignWindowServiceConfig?.allowTiling ||
    foreignWindowServiceConfig?.allowMinimize ||
    foreignWindowServiceConfig?.allowMaximize;
export const getPermission = (finsembleConfig, myConfig, permission) => {
    const myWindowConfig = myConfig?.window;
    const foreign = myConfig?.foreign;
    const foreignWindowService = foreign?.services?.windowService ?? foreign?.services?.dockingService;
    const windowManager = foreign?.components?.["Window Manager"];
    const globalWindowManager = finsembleConfig?.["Window Manager"];
    const fsblHeader = windowManager?.FSBLHeader;
    if (typeof fsblHeader !== "boolean") {
        if (permission === "close") {
            return reverseBoolean(fsblHeader?.hideClose) ?? true;
        }
        if (permission === "maximize") {
            return foreignWindowService?.allowMaximize ?? reverseBoolean(fsblHeader?.hideMaximize) ?? true;
        }
        if (permission === "minimize") {
            return foreignWindowService?.allowMinimize ?? reverseBoolean(fsblHeader?.hideMinimize) ?? true;
        }
        if (permission === "alwaysOnTop") {
            return windowManager?.alwaysOnTopIcon ?? globalWindowManager?.alwaysOnTopIcon ?? false;
        }
    }
    else {
        if (permission === "close" || permission === "maximize" || permission === "minimize") {
            return fsblHeader !== false;
        }
        if (permission === "alwaysOnTop") {
            return false;
        }
    }
    const globalServiceConfig = finsembleConfig?.servicesConfig;
    const legacyDockingService = globalServiceConfig?.docking;
    const globalWindowService = globalServiceConfig?.windowService ?? globalServiceConfig?.dockingService ?? legacyDockingService;
    if (permission === "grouping") {
        return ((foreignWindowService?.manageWindowMovement ?? legacyDockingService?.enabled ?? true) &&
            (foreignWindowService?.allowGrouping ?? myWindowConfig?.canDock ?? true));
    }
    if (permission === "tabbing") {
        return ((foreignWindowService?.manageWindowMovement ??
            globalWindowService?.tabbing?.enabled ??
            legacyDockingService?.tabbing?.enabled ??
            true) &&
            (foreignWindowService?.allowTabbing ??
                windowManager?.showTabs ??
                globalWindowManager?.showTabs ??
                reverseBoolean(foreignWindowService?.ignoreTilingAndTabbingRequests) ??
                true));
    }
    return false;
};
const getFinsembleWindowFeatures = (finsembleConfig, myConfig) => {
    const windowServiceConfig = myConfig?.foreign?.services?.windowService;
    const anyAllowIsTrue = anyWindowManagementAllowed(windowServiceConfig);
    const isDockable = !!myConfig?.window?.dockable;
    const FSBLHeader = myConfig?.foreign?.components?.["Window Manager"]?.FSBLHeader;
    const shouldRegisterDocking = anyAllowIsTrue || isDockable || FSBLHeader || windowServiceConfig?.manageWindowMovement === true;
    if (!shouldRegisterDocking || windowServiceConfig?.manageWindowMovement === false) {
        return {
            allowAutoArrange: false,
            snapping: false,
            tiling: false,
            tabbing: false,
            grouping: false,
            canMinimize: true,
            canMaximize: true,
            overwriteStartDocked: false,
            maxWidth: myConfig?.window?.maxWidth === -1 ? null : myConfig?.window?.maxWidth ?? null,
            maxHeight: myConfig?.window?.maxHeight === -1 ? null : myConfig?.window?.maxHeight ?? null,
            minWidth: myConfig?.window?.minWidth === -1 ? null : myConfig?.window?.minWidth ?? null,
            minHeight: myConfig?.window?.minHeight === -1 ? null : myConfig?.window?.minHeight ?? null,
            showTaskbarIcon: myConfig?.window?.options?.showTaskbarIcon ?? null,
            minimizeButton: getPermission(finsembleConfig, myConfig, "minimize"),
            maximizeButton: getPermission(finsembleConfig, myConfig, "maximize"),
            alwaysOnTopButton: getPermission(finsembleConfig, myConfig, "alwaysOnTop"),
            closeButton: getPermission(finsembleConfig, myConfig, "close"),
        };
    }
    const defaultValue = windowServiceConfig?.manageWindowMovement ?? (FSBLHeader || isDockable) ? true : false;
    return {
        allowAutoArrange: windowServiceConfig?.allowAutoArrange ?? defaultValue,
        snapping: !myConfig?.window?.ephemeral && (windowServiceConfig?.allowSnapping ?? defaultValue),
        tiling: windowServiceConfig?.allowTiling ?? defaultValue,
        tabbing: getPermission(finsembleConfig, myConfig, "tabbing"),
        grouping: getPermission(finsembleConfig, myConfig, "grouping"),
        canMinimize: windowServiceConfig?.allowMinimize ?? myConfig?.component?.canMinimize ?? defaultValue,
        canMaximize: windowServiceConfig?.allowMaximize ?? myConfig?.component?.canMaximize ?? defaultValue,
        overwriteStartDocked: myConfig?.foreign?.services?.workspaceService?.global ?? false,
        maxWidth: myConfig?.window?.maxWidth === -1 ? null : myConfig?.window?.maxWidth ?? null,
        maxHeight: myConfig?.window?.maxHeight === -1 ? null : myConfig?.window?.maxHeight ?? null,
        minWidth: myConfig?.window?.minWidth === -1 ? null : myConfig?.window?.minWidth ?? null,
        minHeight: myConfig?.window?.minHeight === -1 ? null : myConfig?.window?.minHeight ?? null,
        showTaskbarIcon: myConfig?.window?.options?.showTaskbarIcon ?? null,
        minimizeButton: getPermission(finsembleConfig, myConfig, "minimize"),
        maximizeButton: getPermission(finsembleConfig, myConfig, "maximize"),
        alwaysOnTopButton: getPermission(finsembleConfig, myConfig, "alwaysOnTop"),
        closeButton: getPermission(finsembleConfig, myConfig, "close"),
    };
};
const maybeProcessAlias = (manifest, appd, logger) => {
    const alias = appd?.hostManifests?.Finsemble?.window?.alias ?? appd?.details?.alias;
    if (!alias) {
        return {};
    }
    const appAsset = (manifest.appAssets ?? []).find((entry) => entry.alias === alias);
    if (!appAsset) {
        logger.warn(`config - Assets entry for ${alias} could not be found. Check manifest-local.json's "manifest" section.`);
        return {};
    }
    return {
        asset: {
            src: appAsset.src
        },
        details: {
            path: "$ASSET_LOCATION$",
            command: appAsset.target
        }
    };
};
const addDocking = (shared, manifest, hostManifestWindow) => {
    const claimScreenArea = manifest.finsemble?.servicesConfig?.docking?.claimSpace?.enabled !== false && manifest.finsemble?.servicesConfig?.window?.claimSpace?.enabled !== false;
    const { dockedHeight, dockable, docked, width } = hostManifestWindow;
    shared.docking = {
        enabled: true,
        claimScreenArea,
        initialPosition: docked,
        allowedPositions: dockable,
        height: dockedHeight,
    };
    if (width) {
        shared.docking.width = width;
    }
    delete shared.placement;
};
const derivePreloads = (manifest, hostManifest) => {
    const preload = hostManifest?.component?.preload ?? [];
    let preloads = [];
    const extensions = manifest?.finsemble?.extensions;
    const legacyGlobal = manifest?.finsemble?.servicesConfig?.launcher?.defaultPreloads;
    if (extensions) {
        const globalPreloads = Object.values(extensions?.preloads ?? {});
        const preloadApplies = (entry) => entry.autoload === "all" || (entry.autoload === "launchable" && (hostManifest?.foreign?.components?.["App Launcher"]?.launchableByUser ?? false));
        preloads = preloads.concat(globalPreloads.flatMap((entry) => preloadApplies(entry) ? [entry.url] : []));
    }
    if (legacyGlobal) {
        preloads = preloads.concat(legacyGlobal.allComponents ?? []);
        if (hostManifest?.foreign?.components?.["App Launcher"]?.launchableByUser) {
            preloads.concat(legacyGlobal.launchableComponents ?? []);
        }
    }
    if (Array.isArray(preload)) {
        preloads = preloads.concat(preload);
    }
    else if (typeof preload === "string") {
        preloads.push(preload);
    }
    else if (preload === false) {
        return [];
    }
    return preloads;
};
const hostManifestToGlueDetailsAndAsset = (glue, logger, manifest, hostManifest, appd) => {
    const deprecatedTargets = ["AssimilationMain.exe", "BloombergBridge.exe"];
    const features = getFinsembleWindowFeatures(manifest?.finsemble, hostManifest);
    const pseudoAppd = {
        appId: appd?.appId ?? "UI Component",
        type: "web",
        ...appd
    };
    const icons = getIcons(pseudoAppd);
    const spawnParams = hostManifest?.window;
    const gdDisplayIndex = getGdDisplayIndex(hostManifest?.window?.monitor);
    const placement = getPlacementSettings(glue, spawnParams, gdDisplayIndex);
    const relativeSettings = getRelativeSettings(glue, spawnParams);
    const alwaysOnTop = spawnParams?.ephemeral ?? spawnParams?.options?.alwaysOnTop ?? false;
    const { asset, details: aliasDetails } = maybeProcessAlias(manifest, appd, logger);
    if (aliasDetails?.command && deprecatedTargets.includes(aliasDetails.command)) {
        logger.warn(`config - appAsset for deprecated target ${aliasDetails.command} not loaded. Skipping app entry ${pseudoAppd.appId}`);
        return { err: null };
    }
    const url = pseudoAppd?.details?.url ?? spawnParams?.url ?? spawnParams?.path;
    const isInDocumentRoot = url?.includes(manifest?.finsemble?.documentRoot);
    const securityPolicy = spawnParams?.options?.securityPolicy ?? isInDocumentRoot ? "trusted" : "untrusted";
    const allowEnvVars = securityPolicy === "trusted";
    const allowClearingCache = securityPolicy === "trusted";
    const allowOSInfo = securityPolicy === "trusted";
    const fsblHeader = hostManifest?.foreign?.components?.["Window Manager"]?.FSBLHeader === false ? false : undefined;
    const showInTaskbar = reverseBoolean(spawnParams?.ephemeral) ?? spawnParams?.options?.showTaskbarIcon ?? fsblHeader ?? true;
    const shared = deleteNullish({
        placement,
        title: appd?.title ?? hostManifest?.component?.displayName ?? appd?.name ?? appd?.appId,
        isSticky: hostManifest?.foreign?.services?.windowService?.allowGrouping ?? hostManifest?.foreign?.services?.windowService?.shouldRegister ?? true,
        channelSelector: manifest.iocd?.channelSelector ?? {
            enabled: hostManifest?.interop?.useLinker !== false
        },
        allowClose: features.closeButton,
        allowMinimize: features.canMinimize,
        allowMaximize: features.canMaximize,
        allowEnvVars,
        allowClearingCache,
        allowOSInfo,
        showCloneButton: hostManifest?.foreign?.components?.["Window Manager"]?.cloneIcon,
        backgroundThrottling: spawnParams?.options?.backgroundThrottling ?? false,
        contextMenuEnabled: spawnParams?.options?.contextMenu ?? true,
        hasSizeAreas: reverseBoolean(spawnParams?.ephemeral) ?? spawnParams?.options?.resizable ?? true,
        hidden: reverseBoolean(spawnParams?.autoShow ?? spawnParams?.options?.autoShow) ?? false,
        maxHeight: features.maxHeight,
        maxWidth: features.maxWidth,
        minHeight: features.minHeight,
        minWidth: features.minWidth,
        onTop: alwaysOnTop ? "always" : false,
        showInTaskbar,
        relativeTo: relativeSettings.relativeTo,
        relativeDirection: relativeSettings.relativeDirection,
        cascade: getCascade(spawnParams),
        urlLoadOptions: spawnParams?.options?.userAgentString,
        devToolsEnable: manifest?.["finsemble-electron-adapter"]?.toggleInspectorHotkey ? true : false,
        taskbar: {
            iconPath: icons.taskbarIcon?.path,
            combineIcons: "sameIcon",
            allowPin: false
        }
    });
    if (hostManifest?.component?.maxReloadAttempts || hostManifest?.component?.reloadTimeout) {
        shared.reloadOptions = {
            timeout: hostManifest?.component?.reloadTimeout ?? 60000,
            maxAttempts: hostManifest?.component?.maxReloadAttempts ?? 1
        };
    }
    if (hostManifest?.window?.data) {
        shared.context = {
            "#finsemble-spawnData": hostManifest?.window?.data
        };
    }
    if (hostManifest?.window?.dockable) {
        addDocking(shared, manifest, hostManifest.window);
    }
    const parameters = appd?.details?.arguments ?? spawnParams?.arguments;
    let exeParameters = typeof parameters === "string" ? parameters : (parameters ?? []).join(" ");
    if (pseudoAppd.type === "native" && hostManifest?.window?.windowType !== "assimilation") {
        exeParameters = updateNativeCmdArgs(exeParameters, appd?.appId);
        if (manifest.iocd?.apiToLogFile === true && !exeParameters.includes("apiToLogFile")) {
            exeParameters = exeParameters.concat(" apiToLogFile=true");
        }
    }
    const { command, path } = splitCommandPath(aliasDetails, pseudoAppd.details?.path);
    const useShellExecute = command?.endsWith(".bat");
    const exe = deleteNullish({
        ...shared,
        command,
        path,
        useShellExecute,
        parameters: exeParameters,
        saveInLayout: {
            command: hostManifest?.foreign?.services?.workspaceService?.persistPath ?? false,
            parameters: hostManifest?.foreign?.services?.workspaceService?.persistArguments ?? true,
        },
        startingContextMode: hostManifest?.window?.windowType === "assimilation" ? "none" : "interop",
        trackingType: hostManifest?.window?.windowType === "assimilation" ? "Process" : "AGM"
    });
    const badPreloads = ["BloombergBridgePreload", "BloombergBridgeClient"];
    const scripts = derivePreloads(manifest, hostManifest).filter((script) => !badPreloads.some((preload) => script.includes(preload)));
    const windowMode = hostManifest?.foreign?.components?.["Window Manager"]?.FSBLHeader === false ? "frameless" :
        getPermission(manifest.finsemble, hostManifest, "tabbing") ? "tab" : "flat";
    const win = deleteNullish({
        ...shared,
        onTop: spawnParams?.options?.alwaysOnTop ? "always" : null,
        contextMenuEnabled: spawnParams?.options?.contextMenu ?? true,
        preloadScripts: {
            scripts,
            useBase64PreloadScripts: false
        },
        url: url ?? "http://about",
        icon: icons.taskbarIcon?.path,
        mode: windowMode
    });
    if (spawnParams?.options?.allowedNavigationUrls) {
        win.navigation = finsembleWhitelistToRegex(spawnParams?.options?.allowedNavigationUrls);
    }
    if (spawnParams?.options?.allowedWindowOpenUrls) {
        win.windowOpen = finsembleWhitelistToRegex(spawnParams?.options?.allowedWindowOpenUrls);
    }
    const citrix = deleteNullish({
        ...shared
    });
    switch (pseudoAppd.type) {
        case "native":
        case "native":
        case "FinsembleNativeWindow":
        case "assimilation":
            if (url && isProtocolHandler(url)) {
                return { data: { details: win, asset, type: "window" } };
            }
            else {
                return { data: { details: exe, asset, type: "exe" } };
            }
        case "citrix":
            return { data: { details: citrix, asset, type: "citrix" } };
        default:
            return { data: { details: win, asset, type: "window" } };
    }
};
const fdc3ToGlueDetailsAndAsset = (glue, logger, manifest, entry) => {
    const hostManifest = entry.hostManifests?.Finsemble;
    return hostManifestToGlueDetailsAndAsset(glue, logger, manifest, hostManifest, entry);
};
const convertFDC3IntentsToGlueIntents = (intents) => {
    if (!intents) {
        return [];
    }
    return Object.entries(intents.listensFor ?? {}).map((entry) => {
        const [key, value] = entry;
        return deleteNullish({
            name: key,
            displayName: value.displayName,
            contexts: value.contexts,
            resultType: value.resultType
        });
    });
};
export const processManifestMacros = (macros, config, logger = consoleLogger) => {
    logger.debug("config - processManifestMacros");
    const replaceMacros = (value) => {
        let resolvedValue = value;
        if (typeof value === "string") {
            const infiniteLoopProtection = Object.entries(macros).length;
            for (let i = 0; i < infiniteLoopProtection; i++) {
                let madeReplacement = false;
                Object.entries(macros).forEach((entry) => {
                    const [macro, macroValue] = entry;
                    if (resolvedValue.includes(macro)) {
                        resolvedValue = resolvedValue.replace(macro, macroValue);
                        madeReplacement = true;
                    }
                });
                if (!madeReplacement) {
                    break;
                }
            }
        }
        return resolvedValue;
    };
    traverse(config, ({ obj, key, value }) => {
        if (typeof value === "string") {
            const resolvedValue = replaceMacros(value);
            obj[key] = resolvedValue;
        }
        else if (Array.isArray(value)) {
            obj[key] = value.map((item) => replaceMacros(item));
        }
    });
    return config;
};
export const expandConfig = (config) => {
    if (!config) {
        return;
    }
    Object.entries(config?.finsemble?.appd ?? {}).forEach((entry) => {
        const value = entry[1];
        config.finsemble.apps = (config.finsemble.apps ?? []).concat(value);
    });
    delete config?.finsemble?.appd;
    Object.entries(config?.finsemble?.components ?? {}).forEach((entry) => {
        const [key, value] = entry;
        config.finsemble.components[key] = deepmerge(defaultUIComponent, value);
    });
    (config?.finsemble?.apps ?? [])?.forEach((entry, index) => {
        if (entry.manifest) {
            entry = config.finsemble.apps[index] = newAppDEntry(entry);
        }
        if (entry.hostManifests?.Finsemble?.appService) {
            config.finsemble.apps[index] = deepmerge(defaultAppService, entry);
        }
    });
};
const maybeAddSpawnOnHotkey = async (glue, logger, definitions) => {
    definitions.forEach(async (definition) => {
        const maybeHotkey = definition.customProperties?.appd?.hostManifests?.Finsemble?.component?.spawnOnHotkey;
        if (maybeHotkey) {
            const { name } = definition;
            const glueKeyString = convertFinsembleKeyArrToGlueKeyString(maybeHotkey);
            logger.debug(`config - Adding spawnOnHotkey ${glueKeyString} = ${name}`);
            glue.hotkeys.register(glueKeyString, async () => {
                const application = glue.appManager.application(name);
                if (application) {
                    const { err } = await toGoLang(() => application.start());
                    if (err) {
                        logger.error(`config - Error spawning ${name} from hotkey ${glueKeyString}`, err);
                    }
                }
            });
        }
    });
};
export const configToGlue = (glue, logger, manifest, appIds) => {
    if (!manifest) {
        return [];
    }
    const apps = appIds ? (manifest.finsemble?.apps ?? []).filter((app) => appIds.includes(app.appId)) : manifest.finsemble?.apps;
    const components = appIds ? objectFlatMap(manifest.finsemble?.components, (component) => {
        return appIds.includes(component.name) ? [component.name] : [];
    }) : manifest.finsemble?.components;
    const componentDefinitions = convertComponentsToGlueDefinition(glue, logger, manifest, components);
    const appsDefinitions = convertAppsToGlueDefinition(glue, logger, manifest, apps);
    const definitions = componentDefinitions.concat(appsDefinitions);
    maybeAddSpawnOnHotkey(glue, logger, definitions);
    return definitions;
};
